import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiRefusal } from '@gatecheckpro/api-client';
import { Card, Notice } from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { FailureNotice } from '../lib/refusal.tsx';
import { takeCeremony } from '../lib/cabs.ts';
import { servesOrigin } from '../lib/origins.ts';

/**
 * Where CABS Identity returns the browser after a sign-in.
 *
 * ## What this page does not do
 *
 * It does not exchange the authorization code, and it holds no PKCE material.
 * The verifier, the nonce and the expected state live in a server-side row from
 * the moment the request started; this page never saw them and could not have
 * kept them safely if it had. So the whole of its job is to hand the redirect
 * back to the server boundary that already owns the ceremony.
 *
 * That is why it posts `currentUrl` rather than a parsed `code` and `state`.
 * Parsing here would move a security decision into the browser, where it is not
 * a decision at all — anything the page could extract, a caller could also
 * fabricate. The server reads the URL, finds the single-use row keyed by the
 * state digest, and refuses if there isn't one.
 *
 * ## What it stores
 *
 * A session, through `api.setSession`, exactly as the password sign-in does —
 * and nothing else. No code, no token from the provider, no verifier, no
 * client secret. A second storage path for CABS sessions would be a second
 * session representation, and the one used less is the one that stops honouring
 * an expiry.
 *
 * ## Which ceremony it completes
 *
 * From what this browser recorded when it started one, never from the URL it
 * was returned to. Only one of the two endpoints can create an account, so
 * selecting it from a query parameter would hand that choice to whoever sent
 * the link. With nothing recorded it completes an ordinary sign-in — the
 * ceremony that can only reach an account this subject is already bound to.
 */
export function CabsCallbackPage() {
  const navigate = useNavigate();
  const { reload } = useSession();
  const [error, setError] = useState<unknown>(null);
  /* A stranger is not a failure of this page; it is a person who has no account here yet. */
  const [unbound, setUnbound] = useState(false);
  /*
   * React runs effects twice in development StrictMode, and the ceremony is
   * single-use: the second attempt would be refused as a replay and would
   * replace a good session with an error. Guarding here keeps that a
   * development-only concern rather than a user-visible one; the server refuses
   * a genuine replay regardless, which is where the guarantee actually lives.
   */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const ceremony = takeCeremony();

    void (async () => {
      try {
        const session = await api.request<{
          accessToken: string;
          accessTokenExpiresAt: string;
          sessionToken: string;
          sessionExpiresAt: string;
          mfaSatisfied: boolean;
          returnTo: string;
        }>(
          ceremony?.purpose === 'FIRST_USE'
            ? '/api/v1/auth/cabs/first-use/callback'
            : '/api/v1/auth/cabs/callback',
          {
            method: 'POST',
            anonymous: true,
            // The whole URL, unparsed. The server owns every check that matters.
            body: {
              currentUrl: window.location.href,
              ...(ceremony?.purpose === 'FIRST_USE' ? { displayName: ceremony.displayName } : {}),
            },
          },
        );

        api.setSession(session);
        await reload();

        /*
         * The destination comes from the server, which validated it against
         * this deployment's configured origins before the browser ever left for
         * CABS. It is *not* read from this page's query string: a destination
         * the caller supplies alongside the redirect is a destination the
         * caller chose, which is the open redirect this whole check exists to
         * prevent.
         *
         * The origin is re-checked here as defence in depth — never rewritten.
         * Reducing a valid organizer or ops destination to an attendee path
         * would strand exactly the people the shared callback exists to serve.
         */
        const target = new URL(session.returnTo);
        if (!servesOrigin(target)) {
          setError(new Error('That destination is not a surface this deployment serves'));
          return;
        }

        if (target.origin === window.location.origin) {
          // `replace`, so the authorization code in this URL leaves the history
          // stack. Going back must not re-present a spent code.
          void navigate(`${target.pathname}${target.search}${target.hash}`, { replace: true });
        } else {
          // A different surface entirely, so a router cannot take us there.
          // `replace` for the same reason.
          window.location.replace(target.toString());
        }
      } catch (failure) {
        // Deliberately not logged. This URL carries an authorization code, and
        // a refusal carries the server's reason; neither belongs in a console
        // that extensions and crash reporters can read.
        if (failure instanceof ApiRefusal && failure.code === 'AUTH_CABS_SUBJECT_NOT_BOUND') {
          setUnbound(true);
          return;
        }
        setError(failure);
      }
    })();
  }, [navigate, reload]);

  /*
   * Offered, never performed. The person is sent back to make the choice explicitly, because an
   * account created for a stranger at the moment they were refused one is implicit provisioning
   * wearing a helpful face.
   */
  if (unbound) {
    return (
      <Card>
        <h1>No GateCheck account yet</h1>
        <Notice tone="info" title="This CABS identity is not linked to a GateCheck account">
          Signing in with CABS reaches an account you have already linked. Create one to get
          started, and this identity will be linked to it.
        </Notice>
        <p>
          <a href="/register">Create a GateCheck account</a>
        </p>
        <p>
          <a href="/sign-in">Back to sign in</a>
        </p>
      </Card>
    );
  }

  if (error !== null) {
    return (
      <Card>
        <h1>Sign-in could not be completed</h1>
        <FailureNotice error={error} />
        <p>
          <a href="/sign-in">Return to sign in</a>
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h1>Completing sign-in</h1>
      <Notice tone="info">Finishing your sign-in with CABS Identity.</Notice>
    </Card>
  );
}
